import { Big } from 'big.js';
import { isNil } from 'lodash';
import type { Token } from '@/api/spec';
import { Side } from '@/common/constants';
import { stringToBig } from '@/utils/bigUtils';

export const PERCENT_TOKEN: Partial<Token> = {
  symbol: '%',
};
export const QUOTE_TOKEN: Partial<Token> = {
  symbol: '$',
};
export const MODE_TOKENS = [QUOTE_TOKEN, PERCENT_TOKEN];

/**
 * Sign of a TP/SL leg's price move: TP-long / SL-short push price up (+1); the opposite legs down (-1).
 * Shared so every surface classifies a leg's direction identically.
 */
export function tpSlSign(side: Side, isTakeProfit: boolean): number {
  const typeSign = isTakeProfit ? 1 : -1;
  const sideSign = side === Side.long ? 1 : -1;
  return typeSign * sideSign;
}

export function calculatePercent(entryPrice: number, targetPrice: number | string, sign: number, leverage: number): number {
  // Percent = ((Target - Entry) / Entry) * sign * Leverage * 100

  return stringToBig(targetPrice)
    .minus(stringToBig(entryPrice))
    .div(stringToBig(entryPrice))
    .mul(sign)
    .mul(leverage)
    .mul(100)
    .toNumber();
}

/**
 * Derives an absolute trigger/limit price from a ROE percent — the exact inverse of calculatePercent.
 * Single source of truth shared by the order-entry form (`calculateTriggerPrice`) and the position
 * TP/SL modal (`useTpSlInputMode`) so the two surfaces can't drift apart (e.g. on leverage handling).
 */
export function triggerPriceFromPercent(
  referencePrice: number | string,
  percent: number | string,
  sign: number,
  leverage: number
): Big {
  const lev = leverage > 0 ? leverage : 1;
  const ratio = stringToBig(percent).div(100).div(lev);
  return stringToBig(referencePrice).times(new Big(1).plus(new Big(sign).times(ratio)));
}

/**
 * PnL in the leg's own sign convention (positive = "gain" for TP / "loss" for SL when `sign`
 * is the tpSlSign leg sign). This feeds the Gain/Loss input round-trip; it is NOT the position's
 * real PnL — use calculateExpectedPositionPnl for display.
 */
export function calculateExpectedPnL({
  entryPrice,
  triggerValue,
  limitValue,
  size,
  sign,
}: {
  entryPrice: number;
  triggerValue?: number | string;
  limitValue?: number | string;
  size: number;
  sign: number;
}) {
  const trigger = isNil(triggerValue) ? undefined : stringToBig(triggerValue);
  const limit = isNil(limitValue) ? undefined : stringToBig(limitValue);

  if (!trigger && !limit) {
    return 0;
  }

  // Use limit price if available, otherwise trigger price
  const execPrice = limit || trigger!;

  return execPrice.minus(entryPrice).mul(sign).mul(size).toNumber();
}

/**
 * Real position PnL at the leg's execution price (positive = profit, negative = loss),
 * independent of which leg is being edited — a stop loss above a long's entry secures
 * profit and must read as one.
 */
export function calculateExpectedPositionPnl({
  entryPrice,
  triggerValue,
  limitValue,
  size,
  side,
}: {
  entryPrice: number;
  triggerValue?: number | string;
  limitValue?: number | string;
  size: number;
  side: Side;
}): number {
  return calculateExpectedPnL({ entryPrice, triggerValue, limitValue, size, sign: side === Side.long ? 1 : -1 });
}
